import { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Checkbox,
  Collapse,
  Divider,
  FormControlLabel,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Fragment } from 'react';
import { DISCLOSURE_MS, REDUCED_MOTION_QUERY, cardSurfaceSx } from '../../theme';
import type { FilterGroup, FilterSidebarProps } from './types';

/**
 * The filters beside the results — and, below desktop, above them.
 *
 * **Two groups, where the designs draw three.** The third is AI Integration, and
 * `ContentSummary` carries no tool list — those are on the full record, which a
 * listing deliberately does not fetch. It comes back when the summary carries
 * one; the same service change owes the method card its mastery target.
 *
 * **Below desktop it collapses, which is what lets it sit above the results.**
 * Stacked open, nine rows is most of a phone screen before the first result;
 * under the list, a control nobody scrolls to is a control nobody uses. On
 * desktop there is a column for it and no toggle — a disclosure over something
 * already visible has nothing to do.
 *
 * **Each group is a `fieldset` with a real `legend`**, which is what says "these
 * four are the stage question" rather than four unrelated checkboxes.
 *
 * **A count of zero greys the row and takes it out of the tab order**, because
 * ticking it could only empty the list. A ticked box that falls to zero stays
 * live — disabling the control someone is using traps them with no way back.
 */
export const FilterSidebar = ({ groups, onReset }: FilterSidebarProps) => {
  const theme = useTheme();

  /**
   * **The breakpoint is read rather than drawn.** A CSS-only version makes the
   * collapsed state the base state, so anything not evaluating media queries
   * sees a shut panel with no way to open it.
   */
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  /**
   * **`Collapse` animates a height in JavaScript**, so unlike the app's CSS it
   * cannot opt itself out of `prefers-reduced-motion` — the timeout is zeroed
   * from here.
   */
  const prefersReducedMotion = useMediaQuery(REDUCED_MOTION_QUERY);
  const [isOpen, setIsOpen] = useState(false);

  const activeCount = groups.reduce(
    (total, group) => total + group.options.filter((option) => option.checked).length,
    0,
  );

  const isExpanded = !isCompact || isOpen;

  return (
    <Box sx={{ ...cardSurfaceSx, padding: 6 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 4,
        }}
      >
        {isCompact ? (
          <Typography
            component="button"
            type="button"
            variant="h3"
            onClick={() => {
              setIsOpen((open) => !open);
            }}
            aria-expanded={isOpen}
            aria-controls={GROUPS_ID}
            /*
              Labelled rather than left to its text content: an accessible name
              is the text nodes concatenated and each one trimmed, so the count
              announces as "Filters(1)" however it is spaced.
            */
            aria-label={activeCount === 0 ? 'Filters' : `Filters, ${String(activeCount)} applied`}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'inherit',
              textAlign: 'left',
            }}
          >
            Filters
            {/*
              So a shut panel still says whether it is doing anything. Omitted at
              zero — "Filters (0)" is noise where "Filters" says the same. The
              gap is the button's flex `gap`, not a space character.
            */}
            {activeCount > 0 && (
              <Box component="span" sx={{ color: 'text.meta' }}>
                ({activeCount})
              </Box>
            )}
            <ExpandMoreIcon
              aria-hidden
              fontSize="small"
              sx={{
                color: 'text.meta',
                transition: `transform ${String(DISCLOSURE_MS)}ms ease-out`,
                transform: isOpen ? 'rotate(180deg)' : 'none',
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            />
          </Typography>
        ) : (
          <Typography variant="h3" component="h2">
            Filters
          </Typography>
        )}

        {onReset !== undefined && (
          <Typography
            component="button"
            type="button"
            variant="overline"
            onClick={onReset}
            sx={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: 'primary.main',
              fontWeight: 500,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Reset all
          </Typography>
        )}
      </Box>

      {/*
        `unmountOnExit` is deliberately off, so `aria-controls` points at
        something real and what is ticked survives being hidden.
      */}
      <Collapse
        in={isExpanded}
        id={GROUPS_ID}
        timeout={prefersReducedMotion ? 0 : DISCLOSURE_MS}
      >
        {groups.map((group) => (
          <Fragment key={group.legend}>
            <Divider sx={{ marginBlock: 6 }} />
            <Group group={group} />
          </Fragment>
        ))}
      </Collapse>
    </Box>
  );
};

/** What the toggle points at. One panel per page, so a constant is enough. */
const GROUPS_ID = 'search-filter-groups';

const Group = ({ group }: { group: FilterGroup }) => (
  <Box component="fieldset" sx={{ border: 'none', margin: 0, padding: 0 }}>
    <Typography component="legend" variant="h4" sx={{ padding: 0, marginBottom: 3 }}>
      {group.legend}
    </Typography>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {group.options.map((option) => {
        const isUnreachable = option.count === 0 && !option.checked;

        return (
          <Box
            key={option.value}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3 }}
          >
            <FormControlLabel
              disabled={isUnreachable}
              control={
                <Checkbox
                  size="small"
                  checked={option.checked}
                  onChange={() => {
                    group.onToggle(option.value);
                  }}
                  sx={{ paddingBlock: 1 }}
                />
              }
              label={
                <Typography variant="body2" component="span">
                  {option.label}
                </Typography>
              }
              // Outside the label on purpose, so the checkbox's own accessible
              // name stays "Build" rather than "Build 3". The row below reads it.
              sx={{ marginRight: 0, minWidth: 0 }}
            />

            <Typography
              variant="caption"
              component="span"
              // Announced here, so the number is not silently visual.
              aria-label={`${String(option.count)} matching`}
              sx={{ color: isUnreachable ? 'text.disabled' : 'text.meta', flexShrink: 0 }}
            >
              {option.count}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Box>
);
